<!--
  PuppyCollapseItem 组件。
  定位：
  - 对 Naive UI 的 n-collapse-item 做统一封装，作为属性栏中折叠区的标准子组件。
  - 统一处理“带复选框的折叠区”场景，避免各业务面板重复拼装 header-extra 和启用/禁用逻辑。
  功能：
  - 透传 n-collapse-item 的常用属性与插槽。
  - checkbox=true 时，在 header-extra 区域注入复选框，并保留外部 header-extra 内容。
  - checkValue=false 时，默认插槽内部整体进入禁用状态。
-->
<template>
  <n-collapse-item v-bind="collapseItemAttrs">
    <template v-if="$slots.header" #header="slotProps">
      <slot name="header" v-bind="slotProps" />
    </template>

    <template #header-extra="slotProps">
      <div class="puppy-collapse-item__header-extra">
        <n-checkbox
          v-if="checkbox"
          :checked="resolvedCheckValue"
          :disabled="headerCheckboxDisabled"
          @update:checked="handleCheckValueUpdate"
          @click.stop.prevent
        />
        <slot name="header-extra" v-bind="slotProps" />
      </div>
    </template>

    <template #default>
      <fieldset
        class="puppy-collapse-item__fieldset"
        :disabled="isContentDisabled"
      >
        <slot />
      </fieldset>
    </template>
  </n-collapse-item>
</template>

<script setup lang="ts">
/**
 * PuppyCollapseItem 组件的实现层。
 *
 * 设计目标：
 * - 让折叠区在业务层保持统一交互语义。
 * - 通过 checkbox / checkValue 两个字段表达“折叠区是否启用”。
 * - 通过透传 attrs 的方式兼容 NCollapseItem 的常用参数。
 */
import { computed, useAttrs } from 'vue';
import { NCheckbox, NCollapseItem } from 'naive-ui';

defineOptions({
  inheritAttrs: false,
});

interface PuppyCollapseItemProps {
  /**
   * 是否在 header-extra 区域显示复选框。
   */
  checkbox?: boolean;
  /**
   * 复选框状态，支持 v-model:checkValue。
   */
  checkValue?: boolean;
  /**
   * 是否禁用标题区复选框。
   */
  checkboxDisabled?: boolean;
}

const props = withDefaults(defineProps<PuppyCollapseItemProps>(), {
  checkbox: false,
  checkValue: true,
  checkboxDisabled: false,
});

const emit = defineEmits<{
  /**
   * 同步复选框状态。
   */
  (event: 'update:checkValue', value: boolean): void;
}>();

const attrs = useAttrs();

/**
 * 透传给 NCollapseItem 的属性集合。
 *
 * 说明：
 * - 通过 useAttrs 统一透传 title、name、disabled、displayDirective 等常用参数。
 * - 该组件只消费 checkbox / checkValue，因此不会干扰上层对折叠项的标准配置。
 */
const collapseItemAttrs = computed<Record<string, unknown>>(() => ({
  ...attrs,
}));

/**
 * 当前复选框可用状态。
 *
 * 说明：
 * - 当 checkbox=false 时，不渲染复选框，也不影响内容区。
 * - 当 checkbox=true 时，复选框状态由 checkValue 控制。
 */
const resolvedCheckValue = computed(() => Boolean(props.checkValue));

/**
 * 默认插槽是否需要整体禁用。
 */
const isContentDisabled = computed(() => props.checkbox && !resolvedCheckValue.value);

/**
 * header 区复选框是否需要禁用。
 *
 * 说明：
 * - 这里保留为独立计算属性，后续如需接入更细粒度的只读态，可以直接扩展。
 */
const headerCheckboxDisabled = computed(() => props.checkboxDisabled);

/**
 * 更新折叠区启用状态。
 *
 * @param value 新的启用状态。
 */
function handleCheckValueUpdate(value: boolean): void {
  emit('update:checkValue', value);
}
</script>

<style scoped>
.puppy-collapse-item__header-extra {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.puppy-collapse-item__fieldset {
  min-inline-size: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.puppy-collapse-item__fieldset:disabled {
  opacity: 0.6;
}
</style>
